import { useEffect, useRef } from 'react';
import { imageAltText } from './carouselIndex';
import { galleryThumbSizeClass } from './scanImageTabs';

interface ScanImageStripProps {
    names: readonly string[];
    urls: readonly string[];
    current: number;
    imageNoun: string;
    fullscreen: boolean;
    onSelect: (index: number) => void;
}

const HALF = 2;

const THUMB_LOOK = {
    base: 'bg-white focus-visible:ring-gray-400',
    current: 'border-gray-900 ring-1 ring-gray-900',
    other: 'border-gray-200 opacity-70 hover:opacity-100'
} as const;

export function ScanImageStrip({
    names,
    urls,
    current,
    imageNoun,
    fullscreen,
    onSelect
}: ScanImageStripProps) {
    const size = galleryThumbSizeClass(fullscreen);
    const stripRef = useRef<HTMLUListElement>(null);

    // Scroll the strip itself rather than scrollIntoView, which would also scroll the details pane.
    useEffect(() => {
        const strip = stripRef.current;
        const item = strip?.children[current];
        if (!strip || !(item instanceof HTMLElement)) return;
        const left = item.offsetLeft - strip.clientWidth / HALF + item.clientWidth / HALF;
        strip.scrollTo({ left, behavior: 'smooth' });
    }, [current, fullscreen]);

    return (
        <ul
            ref={stripRef}
            className={`relative flex gap-1.5 overflow-x-auto scrollbar-hide py-0.5 ${fullscreen ? 'mx-auto w-fit max-w-full shrink-0' : ''}`}
        >
            {names.map((name, index) => {
                const isCurrent = index === current;
                return (
                    <li key={name} className="shrink-0">
                        <button
                            type="button"
                            onClick={() => onSelect(index)}
                            aria-label={`Show ${imageAltText(imageNoun.toLowerCase(), index, names.length)}`}
                            aria-current={isCurrent ? 'true' : undefined}
                            className={`block ${size} overflow-hidden rounded-md border transition focus:outline-none focus-visible:ring-2 ${THUMB_LOOK.base} ${
                                isCurrent ? THUMB_LOOK.current : THUMB_LOOK.other
                            }`}
                        >
                            {urls[index] && (
                                <img
                                    src={urls[index]}
                                    alt=""
                                    draggable={false}
                                    className="h-full w-full object-cover"
                                />
                            )}
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}
